/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react'
import Typography from '@material-ui/core/Typography'
import TabContext from '@material-ui/lab/TabContext'
import Tab from '@material-ui/core/Tab'
import TabList from '@material-ui/lab/TabList'
import TabPanel from '@material-ui/lab/TabPanel'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'

import { useNotificationSettingsStyles } from './notification-settings.styles'
import {
  NOTIFICATIONS_SERVICE_TYPE,
  getNotificationsServicesTypeOptions,
  useMutationSetNotificationSettings,
  getNotificationsSettingsSelectOptions,
  NOTIFICATION_SETTINGS_VARIANT,
  getNotificationsSettings,
  useQueryNotificationsSettings,
  useMutationSaveNotificationsSettings,
  NotificationSettingsCategoryKeys,
  NotificationSettingsCard,
  NotificationSettingsCategoryKey,
  useSelectSetAllSettingsState
} from '../common'
import { components } from '../../../generated'
import { TranslationDictionary } from 'src/utils'

type NotificationSettingsProps = {
  service?: components['schemas']['AlertServiceType']
  translate?: TranslationDictionary['data']
}

export const NotificationSettings: React.FC<NotificationSettingsProps> = (
  props
) => {
  const classes = useNotificationSettingsStyles()

  const [serviceType, setServiceType] = useState<
    components['schemas']['AlertServiceType']
  >(props.service ?? NOTIFICATIONS_SERVICE_TYPE.OFFICE)

  const { data: notificationsSettings } =
    useQueryNotificationsSettings(serviceType)

  const mutationSaveSettings = useMutationSaveNotificationsSettings(serviceType)

  const stateSelect = useSelectSetAllSettingsState(serviceType)

  const mutationSetNotificationSettingsService =
    useMutationSetNotificationSettings(serviceType)

  const handleClick = () => {
    mutationSetNotificationSettingsService.mutate({
      value: NOTIFICATION_SETTINGS_VARIANT.ALL
    })
  }

  const handleChangeSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as
      | components['schemas']['AlertSettingsValues']
      | 'custom'

    if (value !== NOTIFICATION_SETTINGS_VARIANT.CUSTOM) {
      mutationSetNotificationSettingsService.mutate({
        value
      })
    }
  }

  const handleChangeSettingOption =
    (key: NotificationSettingsCategoryKeys) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target
        .value as components['schemas']['AlertSettingsValues']

      const fieldName = event.target.name

      mutationSaveSettings.mutate({
        group: key,
        option: fieldName,
        value
      })
    }

  return (
    <>
      <Typography variant="h4" className={classes.title}>
        {props.translate?.['office-site']['settings-page']['settings-alert']}
      </Typography>
      <div className={classes.root}>
        <TabContext value={serviceType}>
          <div className={classes.tabContainer}>
            <TabList
              textColor="secondary"
              onChange={(_, value) => setServiceType(value)}
            >
              {getNotificationsServicesTypeOptions(props.translate).map(
                ([key, value]) => (
                  <Tab
                    key={key}
                    label={value}
                    value={key}
                    className={classes.tab}
                  />
                )
              )}
            </TabList>
            <TextField
              value={stateSelect}
              select
              onChange={handleChangeSelect}
              size="small"
              helperText={
                props.translate?.['personal-account']['settings-page'][
                  'description-notification'
                ]
              }
              className={classes.select}
            >
              {getNotificationsSettingsSelectOptions(props.translate).map(
                ([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                )
              )}
              {stateSelect === NOTIFICATION_SETTINGS_VARIANT.CUSTOM && (
                <MenuItem value={NOTIFICATION_SETTINGS_VARIANT.CUSTOM} disabled>
                  {props.translate?.['custom-title']}
                </MenuItem>
              )}
            </TextField>
          </div>
          {getNotificationsServicesTypeOptions(props.translate).map(([key]) => (
            <TabPanel key={key} value={key} className={classes.tabPanel}>
              {getNotificationsSettings(props.translate)[serviceType].map(
                (setting: {
                  key: React.Key | null | undefined
                  title: string
                  fields: {
                    key: React.Key | null | undefined
                    defaultValue: any
                    name:
                      | boolean
                      | React.ReactChild
                      | React.ReactFragment
                      | React.ReactPortal
                      | null
                      | undefined
                  }[]
                }) => {
                  const key = setting.key as NotificationSettingsCategoryKeys
                  const settings = notificationsSettings?.[key]

                  return (
                    <NotificationSettingsCard
                      key={setting.key}
                      title={setting.title}
                    >
                      {setting.fields?.map(
                        (field: {
                          key: any
                          defaultValue: any
                          name:
                            | boolean
                            | React.ReactChild
                            | React.ReactFragment
                            | React.ReactPortal
                            | null
                            | undefined
                        }) => {
                          const fieldKey =
                            field.key as keyof NotificationSettingsCategoryKey

                          const value =
                            settings?.[fieldKey] ?? field.defaultValue
                          const isModels = key === 'options-shop-models'
                          const notifications_options = isModels
                            ? [
                                [
                                  'all',
                                  props.translate?.['personal-account'][
                                    'settings-page'
                                  ]['all-notifications']
                                ],
                                [
                                  'disabled',
                                  props.translate?.['personal-account'][
                                    'settings-page'
                                  ]['disable']
                                ],
                                [
                                  'internal',
                                  props.translate?.['personal-account'][
                                    'settings-page'
                                  ]['only-service']
                                ],
                                [
                                  'email',
                                  props.translate?.['personal-account'][
                                    'settings-page'
                                  ]['only-email']
                                ]
                              ]
                            : getNotificationsSettingsSelectOptions(
                                props.translate
                              )

                          return (
                            <div key={field.key}>
                              <Typography variant="body1" color="primary">
                                {field.name}
                              </Typography>
                              <TextField
                                select
                                name={field.key}
                                onChange={handleChangeSettingOption(key)}
                                value={value}
                                className={classes.selectOptionSetting}
                              >
                                {notifications_options.map(([key, value]) => (
                                  <MenuItem key={key} value={key}>
                                    {value}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </div>
                          )
                        }
                      )}
                    </NotificationSettingsCard>
                  )
                }
              )}
            </TabPanel>
          ))}
        </TabContext>
        <Button
          color="secondary"
          variant="contained"
          className={classes.button}
          onClick={handleClick}
          disabled={stateSelect === NOTIFICATION_SETTINGS_VARIANT.ALL}
        >
          {
            props.translate?.['personal-account']['settings-page'][
              'return-to-default'
            ]
          }
        </Button>
      </div>
    </>
  )
}
